/**
 * The individual view's shared rules (Phase 32; PRD 6.6, 7.2; TRD 10.2 `/forecast/people/:id`;
 * Design Review O-11): the filter keys the page reads, the two metrics that must never share a
 * scale, the contributors and the stack the allocation chart is drawn in, the capacity sentence,
 * the words for Workamajig's billable utilization, and the export rows.
 *
 * Two utilization definitions coexist on this page and nowhere else (O-11). **Allocation** is
 * this tool's: the run's allocated hours over the person's frozen capacity, banded by the run's
 * thresholds in the four reserved status colours. **Billable utilization** is Workamajig's: the
 * hours its report counts as billable, over the week's net hours, read against Workamajig's
 * own healthy band. The rule from the PRD is that the two are never mixed on one scale, so this
 * module gives the second its own band vocabulary, drawn in neutral ink and never in a status
 * colour, and the page draws it in its own chart.
 *
 * Pure: no DOM. Shared by the card, its tests and the server.
 */
import { MAX_TYPE_HUES, OTHER_SLOT, TYPE_SLOTS, assignTypePalette } from './palette.js';
import { col } from './table.js';
import { LAYERS, layerOf } from './layers.js';

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/** The filter-bar keys the page reads (`apiParams`): the run, the window and the pipeline mode. */
export const PERSON_PARAM_KEYS = Object.freeze(['run_id', 'from', 'weeks', 'pipeline']);

// ---------------------------------------------------------------------------------------------
// Billable utilization: Workamajig's metric, Workamajig's band

/**
 * Workamajig's healthy band for billable utilization (PRD 6.6: "currently 72-82%") and the
 * bounds its report flags red at, as its `get_utilization` tool documents them. Fractions.
 */
export const BILLABLE_BAND = Object.freeze({
  low: 0.72,
  high: 0.82,
  red_below: 0.65,
  red_above: 0.9,
});

/**
 * The words for a billable figure against Workamajig's band. Deliberately not the allocation
 * bands: a person at 92% billable is over Workamajig's band and would be "healthy" on the
 * allocation scale, and the page must not let the two read as one.
 */
export const BILLABLE_STATES = Object.freeze([
  Object.freeze({ key: 'well_below', label: 'Well below the band' }),
  Object.freeze({ key: 'below', label: 'Below the band' }),
  Object.freeze({ key: 'in_band', label: 'In the band' }),
  Object.freeze({ key: 'above', label: 'Above the band' }),
  Object.freeze({ key: 'well_above', label: 'Well above the band' }),
]);
const BILLABLE_BY_KEY = new Map(BILLABLE_STATES.map((s) => [s.key, s]));

/** Where a billable utilization sits against the band; null with no figure. */
export function billableState(utilization, band = BILLABLE_BAND) {
  const u = num(utilization);
  if (u === null || !Number.isFinite(u)) return null;
  if (u < band.red_below) return 'well_below';
  if (u < band.low) return 'below';
  if (u <= band.high) return 'in_band';
  if (u <= band.red_above) return 'above';
  return 'well_above';
}

export function billableStateLabel(key) {
  return BILLABLE_BY_KEY.get(key)?.label ?? null;
}

/**
 * The sentence beneath the billable chart, which says whose number this is and what it is
 * divided by: "Workamajig counts 70.8 h of 160.8 h logged as billable over 12 weeks · 15% of
 * 468 h net · reference only". Says when Workamajig itself reports no basis.
 * @param {{ totals: object, window: object, basis: string }} b   the `billable_utilization` block
 * @param {{ hours: Function, percent: Function, count: Function }} fmt
 */
export function billableSentence(b, { hours, percent, count }) {
  const t = b?.totals;
  if (!t || !t.weeks_reported)
    return 'Workamajig has reported no billable hours for this window yet: the sync pulls its utilization report for the trailing complete weeks.';
  const head = `Workamajig counts ${hours(t.billable_hours)} of ${hours(t.total_logged_hours)} logged as billable over ${count(t.weeks_reported)} ${t.weeks_reported === 1 ? 'week' : 'weeks'}`;
  const ratio =
    t.utilization === null || t.utilization === undefined
      ? 'no net hours to divide by'
      : `${percent(t.utilization)} of ${hours(t.net_hours)} net`;
  return `${head} · ${ratio} · reference only`;
}

/**
 * Why the basis is this tool's: Workamajig's report carries none for this account.
 * @param {{ weeks: Array<{ source_basis_hours: number | null, source_flag: string | null }> }} b
 */
export function basisSentence(b) {
  const weeks = (b?.weeks ?? []).filter((w) => w.billable_hours !== null);
  if (!weeks.length) return null;
  const noBasis = weeks.every((w) => !(num(w.source_basis_hours) > 0));
  return noBasis
    ? 'Workamajig reports no capacity basis for this account (its own percentage reads 0%), so the billable hours are divided by this tool’s net hours for the week: contracted hours less time off.'
    : 'The billable hours are divided by this tool’s net hours for the week — contracted hours less time off — not by Workamajig’s own basis, so the two metrics share a denominator.';
}

// ---------------------------------------------------------------------------------------------
// Allocation: the contributors and the stack

/** The neutral segment for the person's own non-project load, as the ribbon draws it. */
export const NONPROJECT_SEGMENT = Object.freeze({
  key: 'nonproject',
  label: 'Non-project',
  token: OTHER_SLOT.var,
});

/** How many contributors wear their own hue before the rest fold. One slot is kept for the fold. */
export const MAX_CONTRIBUTOR_HUES = MAX_TYPE_HUES - 1;

/** The fold: the projects and deals past the hued ones, on the last type slot. */
export const OTHER_CONTRIBUTORS = Object.freeze({
  key: 'other',
  label: 'Other work',
  token: TYPE_SLOTS[MAX_TYPE_HUES - 1].var,
});

/** The key a week's entity and a contributor row share. */
export const contributorKey = (e) => `${e.entity_type}:${e.entity_id}`;

/**
 * The hue each contributor wears: the projects, retainers and deals by hours take the type
 * slots in slot order (the bench's rule, hours as the volume), the rest fold into "Other work",
 * and the non-project load is always the neutral slot. No project type is drawn on this page,
 * so the slots are free and the adjacent-pair validation carries over.
 * @param {Array<{ key: string, entity_type: string, name: string, hours: number }>} contributors
 */
export function contributorSlots(contributors) {
  const named = (contributors ?? []).filter((c) => c.entity_type !== 'nonproject');
  const assigned = assignTypePalette(
    named.map((c) => ({ value: c.key, label: c.name, volume: num(c.hours) ?? 0, priority: 1 })),
  );
  return new Map(assigned.map((a) => [a.value, { slot: a.slot, hue: a.hue, token: a.var }]));
}

/**
 * The segments the stack is drawn in, bottom-up: non-project first (the load that is there
 * whatever happens, as in the ribbon), then the hued contributors by hours, then the fold.
 * @param {Array<object>} contributors  the response's `allocation.contributors`
 */
export function foldContributors(contributors) {
  const slots = contributorSlots(contributors);
  const drawn = [];
  const folded = [];
  let nonproject = null;
  for (const c of contributors ?? []) {
    if (c.entity_type === 'nonproject') {
      // Keeps the contributor's own key (the entity's), so the stack can find its hours.
      nonproject = {
        ...c,
        label: NONPROJECT_SEGMENT.label,
        token: NONPROJECT_SEGMENT.token,
        fill: 'solid',
      };
      continue;
    }
    const s = slots.get(c.key);
    if (s && s.slot <= MAX_CONTRIBUTOR_HUES)
      drawn.push({ ...c, token: s.token, fill: fillForTier(c.confidence_tier) });
    else folded.push({ ...c });
  }
  return {
    nonproject,
    drawn,
    other: folded.length ? { ...OTHER_CONTRIBUTORS, fill: 'solid', contributors: folded } : null,
  };
}

/** The fill a confidence tier asks for (PRD 7.3), restated here so the server can export it too. */
export function fillForTier(tier) {
  if (tier === 'medium') return 'hatch';
  if (tier === 'low') return 'dots';
  return 'solid';
}

/** The segments of a fold in drawing order, for the legend. */
export function foldSegments(fold) {
  return [
    ...(fold.nonproject ? [fold.nonproject] : []),
    ...fold.drawn,
    ...(fold.other ? [fold.other] : []),
  ];
}

/**
 * One week's stack: `[from, to]` per segment, from the week's entities, summing to the week's
 * allocated hours by construction. An entity on no contributor row (it should not happen: the
 * rows are built from the same rows) still reaches the top of the stack.
 * @param {{ allocated_hours: number, entities: Array<{ entity_type: string, entity_id: string, hours: number }> }} week
 * @param {ReturnType<typeof foldContributors>} fold
 */
export function weekStack(week, fold) {
  const by = new Map();
  for (const e of week?.entities ?? []) {
    const k = contributorKey(e);
    by.set(k, round2((by.get(k) ?? 0) + (num(e.hours) ?? 0)));
  }
  let base = 0;
  const segment = (key, label, token, fill, hours, extra = {}) => {
    const s = { key, label, token, fill, from: base, to: round2(base + hours), hours, ...extra };
    base = s.to;
    return s;
  };
  const segments = [];
  const known = new Set();
  if (fold.nonproject) {
    known.add(fold.nonproject.key);
    segments.push(
      segment(
        fold.nonproject.key,
        fold.nonproject.label,
        fold.nonproject.token,
        'solid',
        by.get(fold.nonproject.key) ?? 0,
      ),
    );
  }
  for (const c of fold.drawn) {
    known.add(c.key);
    segments.push(segment(c.key, c.name, c.token, c.fill, by.get(c.key) ?? 0));
  }
  if (fold.other) {
    const parts = fold.other.contributors
      .map((c) => {
        known.add(c.key);
        return { key: c.key, label: c.name, hours: by.get(c.key) ?? 0 };
      })
      .filter((p) => p.hours > 0);
    segments.push(
      segment(
        fold.other.key,
        fold.other.label,
        fold.other.token,
        'solid',
        round2(parts.reduce((s, p) => s + p.hours, 0)),
        { contributors: parts },
      ),
    );
  }
  const rest = round2(
    [...by.entries()].filter(([k]) => !known.has(k)).reduce((s, [, h]) => s + h, 0),
  );
  if (rest > 0) segments.push(segment('unlisted', 'Unlisted', OTHER_SLOT.var, 'solid', rest));
  return segments;
}

/** The top of the allocation axis: the largest week's demand or capacity, whichever is higher. */
export function allocationScaleMax(weeks) {
  let max = 0;
  for (const w of weeks ?? [])
    max = Math.max(max, num(w.allocated_hours) ?? 0, num(w.capacity_hours) ?? 0);
  return max;
}

/** The top of the billable axis: at least the band's red line, else the largest week. */
export function billableScaleMax(weeks, band = BILLABLE_BAND) {
  let max = Math.max(1, band.red_above);
  for (const w of weeks ?? []) max = Math.max(max, num(w.utilization) ?? 0);
  return max;
}

/** The layer vocabulary the table splits a week by, in stack order. */
export const LAYER_COLUMNS = Object.freeze(LAYERS.map((l) => ({ key: l.key, label: l.label })));

/** The layer an entity's hours belong to. */
export const layerOfEntity = (e) => layerOf(e.source_mode);

// ---------------------------------------------------------------------------------------------
// Sentences

/**
 * The capacity sentence for one week: "40 h contracted − 8 h time off − 6.2 h non-project =
 * 25.8 h available", said in the run's own mode (in demand mode the denominator is the net
 * hours and non-project is a layer of the stack, so the sentence stops at net).
 * @param {object} w   a week of `allocation.weeks`
 * @param {'net_hours' | 'available_hours'} denominator
 * @param {{ hours: Function }} fmt
 */
export function capacitySentence(w, denominator, { hours }) {
  if (!w) return '';
  const parts = [`${hours(w.gross_hours)} contracted`];
  const off = round2(
    (num(w.pto_hours) ?? 0) + (num(w.holiday_hours) ?? 0) + (num(w.reduced_hours) ?? 0),
  );
  if (off > 0) parts.push(`− ${hours(off)} time off`);
  if (denominator === 'available_hours') {
    if ((num(w.nonproject_hours) ?? 0) > 0)
      parts.push(`− ${hours(w.nonproject_hours)} non-project`);
    return `${parts.join(' ')} = ${hours(w.available_hours)} available`;
  }
  return `${parts.join(' ')} = ${hours(w.net_hours)} net`;
}

/** What the denominator is called, for the legend and the table. */
export function denominatorLabel(denominator) {
  return denominator === 'available_hours' ? 'Available capacity' : 'Net capacity';
}

/**
 * The time off in the window as one line: "16 h time off over 3 weeks (8 h PTO, 8 h holiday)".
 * @param {Array<object>} weeks
 * @param {{ hours: Function, count: Function }} fmt
 */
export function timeOffSentence(weeks, { hours, count }) {
  let pto = 0;
  let holiday = 0;
  let reduced = 0;
  let n = 0;
  for (const w of weeks ?? []) {
    const p = num(w.pto_hours) ?? 0;
    const h = num(w.holiday_hours) ?? 0;
    const r = num(w.reduced_hours) ?? 0;
    if (p + h + r > 0) n++;
    pto += p;
    holiday += h;
    reduced += r;
  }
  const total = round2(pto + holiday + reduced);
  if (total <= 0) return 'No time off in the window.';
  const parts = [];
  if (pto > 0) parts.push(`${hours(round2(pto))} PTO`);
  if (holiday > 0) parts.push(`${hours(round2(holiday))} holiday`);
  if (reduced > 0) parts.push(`${hours(round2(reduced))} reduced hours`);
  return `${hours(total)} time off over ${count(n)} ${n === 1 ? 'week' : 'weeks'} (${parts.join(', ')})`;
}

// ---------------------------------------------------------------------------------------------
// Tables

/** One row per week of the allocation, with the cell's fields and the layer split. */
export function allocationExportRows(alloc) {
  return (alloc?.weeks ?? []).map((w) => ({
    week_start: w.week_start,
    week_end: w.week_end,
    allocated_hours: w.allocated_hours,
    capacity_hours: w.capacity_hours,
    headroom_hours: w.headroom_hours,
    utilization: w.utilization,
    band: w.band,
    ...Object.fromEntries(LAYER_COLUMNS.map((l) => [`layer_${l.key}`, w.layers?.[l.key] ?? 0])),
    explicit_hours: w.explicit_hours,
    inferred_hours: w.inferred_hours,
    high_confidence_hours: w.high_confidence_hours,
    projects: w.projects,
    opportunities: w.opportunities,
    gross_hours: w.gross_hours,
    pto_hours: w.pto_hours,
    holiday_hours: w.holiday_hours,
    reduced_hours: w.reduced_hours,
    net_hours: w.net_hours,
    nonproject_capacity_hours: w.nonproject_hours,
    available_hours: w.available_hours,
  }));
}

/** One row per contributing project, retainer, deal or the non-project load. */
export function contributorExportRows(alloc) {
  return (alloc?.contributors ?? []).map((c) => ({
    key: c.key,
    entity_type: c.entity_type,
    entity_id: c.entity_id,
    number: c.source_project_number ?? '',
    name: c.name ?? '',
    source_mode: c.source_mode,
    confidence_tier: c.confidence_tier,
    layer: c.layer,
    hours: c.hours,
    share: c.share,
    weeks_with_hours: c.weeks_with_hours,
    first_week: c.first_week,
    last_week: c.last_week,
  }));
}

/** One row per week of the billable reference: Workamajig's figures, this tool's basis, the ratio. */
export function billableExportRows(b) {
  return (b?.weeks ?? []).map((w) => ({
    week_start: w.week_start,
    week_end: w.week_end,
    billable_hours: w.billable_hours,
    nonbillable_hours: w.nonbillable_hours,
    total_logged_hours: w.total_logged_hours,
    gross_hours: w.gross_hours,
    pto_hours: w.pto_hours,
    holiday_hours: w.holiday_hours,
    net_hours: w.net_hours,
    utilization: w.utilization,
    state: billableState(w.utilization, b?.healthy_band ?? BILLABLE_BAND),
    source_basis_hours: w.source_basis_hours,
    source_utilization: w.source_utilization,
    source_flag: w.source_flag,
    synced_at: w.synced_at,
  }));
}

// ---------------------------------------------------------------------------------------------
// The picker

/**
 * The people the picker lists, from the bootstrap's scoped vocabulary, narrowed by a search
 * over name, department and office.
 * @param {Array<{ id: string, display_name: string, department: string | null, office: string | null }>} people
 * @param {string} q
 */
export function filterPeople(people, q) {
  const needle = String(q ?? '')
    .trim()
    .toLowerCase();
  const rows = (people ?? []).filter(
    (p) =>
      !needle ||
      [p.display_name, p.department, p.office].some((v) =>
        String(v ?? '')
          .toLowerCase()
          .includes(needle),
      ),
  );
  return rows.sort((a, b) => String(a.display_name).localeCompare(String(b.display_name)));
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)
//
// Specifications, not renderings: the screen decorates them with `withFormats` and the CSV
// writer reads the same list. The capacity column's label depends on which denominator the run
// used, so it takes it.

/** One row per week of the person's allocation. */
export function personWeekColumns({ denominator = null } = {}) {
  return [
    col.week('week_start', 'Week', { type: 'string' }),
    col.hours('allocated_hours', 'Allocated'),
    col.hours('capacity_hours', denominatorLabel(denominator)),
    col.hours('headroom_hours', 'Headroom'),
    col.percent('utilization', 'Allocation'),
    col.text('band', 'State', { csv: (v) => v ?? '' }),
    ...LAYER_COLUMNS.map((l) => col.hours(`layer_${l.key}`, l.label)),
    col.hours('high_confidence_hours', 'High confidence'),
    col.count('projects', 'Projects'),
    col.count('opportunities', 'Deals'),
    col.hours('gross_hours', 'Contracted'),
    col.hours('pto_hours', 'PTO'),
    col.hours('holiday_hours', 'Holiday'),
    col.hours('reduced_hours', 'Reduced'),
    col.hours('net_hours', 'Net'),
    col.hours('nonproject_capacity_hours', 'Non-project baseline'),
    col.hours('available_hours', 'Available'),
  ];
}

/** One row per project, retainer, deal or non-project load the person's hours are on. */
export function personContributorColumns() {
  return [
    col.text('number', 'Number', { type: 'string' }),
    col.text('name', 'Project, retainer, deal or load', { type: 'string' }),
    col.text('entity_type', 'Kind', { type: 'string' }),
    col.text('source_mode', 'Source', { type: 'string' }),
    col.text('confidence_tier', 'Tier', { type: 'string' }),
    col.text('layer', 'Layer', { type: 'string' }),
    col.hours('hours', 'Hours'),
    col.percent('share', 'Share'),
    col.count('weeks_with_hours', 'Weeks'),
    col.week('first_week', 'From', { type: 'string' }),
    col.week('last_week', 'To', { type: 'string' }),
  ];
}

/** One row per week of billable utilization, this tool's figure beside Workamajig's own. */
export function personBillableColumns() {
  return [
    col.week('week_start', 'Week', { type: 'string' }),
    col.hours('billable_hours', 'Billable (Workamajig)'),
    col.hours('nonbillable_hours', 'Non-billable (Workamajig)'),
    col.hours('total_logged_hours', 'Logged (Workamajig)'),
    col.hours('net_hours', 'Net hours (this tool)'),
    col.percent('utilization', 'Billable utilization'),
    col.text('state', 'Against the band', { type: 'string' }),
    col.hours('gross_hours', 'Contracted'),
    col.hours('pto_hours', 'PTO'),
    col.hours('holiday_hours', 'Holiday'),
    col.hours('source_basis_hours', "Workamajig's basis"),
    col.percent('source_utilization', "Workamajig's own %"),
    col.text('source_flag', "Workamajig's flag", { type: 'string' }),
  ];
}
